import type { TdClient } from "@td/wasm";
import { toApiError } from "./classifyFailure";
import type {
  ApplyResult,
  Command,
  Interval,
  ProjectTree,
  SavedFilter,
  Series,
  SeriesId,
  Sort,
  SyncReport,
  TaskId,
  TaskView,
  When,
} from "./generated";
import { SyncStatusTracker } from "./syncStatusTracker";
import type { ApiEvent, SyncStatus, TaskApi, Unsubscribe } from "./taskApi";

export type WasmClient = Pick<
  TdClient,
  | "query"
  | "get"
  | "projects"
  | "savedFilters"
  | "series"
  | "seriesInstances"
  | "parseDate"
  | "parseCalendarDay"
  | "parseInterval"
  | "apply"
  | "undo"
  | "redo"
  | "sync"
  | "subscribe"
>;

/** `TaskApi` over the in-browser replica compiled from td-client. */
export class WasmTaskApi implements TaskApi {
  private readonly listeners = new Set<(event: ApiEvent) => void>();
  private readonly syncTracker = new SyncStatusTracker();

  constructor(private readonly client: WasmClient) {
    client.subscribe((change) => {
      this.syncTracker.observe(change);
      this.emit({ kind: "change", change });
    });
    this.syncTracker.onStatusChange((status) => {
      this.emit({ kind: "sync-status", status });
    });
  }

  query(filter: string, sort: Sort): Promise<TaskView[]> {
    return this.settle(() => this.client.query(filter, sort));
  }

  get(id: TaskId): Promise<TaskView | undefined> {
    return this.settle(() => this.client.get(id));
  }

  projects(): Promise<ProjectTree> {
    return this.settle(() => this.client.projects());
  }

  savedFilters(): Promise<SavedFilter[]> {
    return this.settle(() => this.client.savedFilters());
  }

  series(id: SeriesId): Promise<Series | undefined> {
    return this.settle(() => this.client.series(id));
  }

  seriesInstances(id: SeriesId): Promise<TaskView[]> {
    return this.settle(() => this.client.seriesInstances(id));
  }

  parseDate(text: string): Promise<When> {
    return this.settle(() => this.client.parseDate(text));
  }

  parseCalendarDay(text: string): Promise<string> {
    return this.settle(() => this.client.parseCalendarDay(text));
  }

  parseInterval(text: string): Promise<Interval> {
    return this.settle(() => this.client.parseInterval(text));
  }

  apply(command: Command): Promise<ApplyResult> {
    return this.settle(() => this.client.apply(command));
  }

  undo(): Promise<ApplyResult> {
    return this.settle(() => this.client.undo());
  }

  redo(): Promise<ApplyResult> {
    return this.settle(() => this.client.redo());
  }

  sync(): Promise<SyncReport> {
    return this.syncTracker.track(() => this.settle(() => this.client.sync()));
  }

  syncStatus(): SyncStatus {
    return this.syncTracker.status();
  }

  subscribe(listener: (event: ApiEvent) => void): Unsubscribe {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(event: ApiEvent): void {
    this.listeners.forEach((listener) => listener(event));
  }

  private async settle<T>(call: () => T | Promise<T>): Promise<T> {
    try {
      return await call();
    } catch (failure) {
      throw toApiError(failure);
    }
  }
}
